import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// =================== Возврат от покупателя ===================

export interface GRFCItem {
  id: string;
  line_number: number;
  nomenclature_id: string;
  characteristic_id: string | null;
  series_id?: string | null;
  quantity: string;
  price: string;
  amount_without_vat: string;
  vat_rate_id: string;
  vat_amount: string;
  total_amount: string;
  cost_amount: string;
  /** Перебивка рахунку запасів рядка (вкладка «Рахунки обліку»). */
  account_code?: string | null;
}

export interface GRFCListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  base_sale_id: string | null;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface GRFC extends GRFCListItem {
  contract_id: string;
  warehouse_id: string;
  amount_without_vat: string;
  vat_amount: string;
  posted_at: string | null;
  comment: string | null;
  /** 0401: накладна покупця на повернення (вхідний документ). */
  buyer_doc_number?: string | null;
  buyer_doc_date?: string | null;
  items: GRFCItem[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface GRFCCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  base_sale_id: string;
  comment?: string | null;
  buyer_doc_number?: string | null;
  buyer_doc_date?: string | null;
}

export const goodsReturnsFromCustomer = {
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number; offset?: number; sort_by?: string; sort_dir?: 'asc' | 'desc'; date_from?: string; date_to?: string }) =>
    api.get<Page<GRFCListItem>>('/goods-returns-from-customer', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<GRFC>(`/goods-returns-from-customer/${id}`).then((r) => r.data),
  createFromSale: (payload: GRFCCreate) =>
    api.post<GRFC>('/goods-returns-from-customer/from-sale', payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/goods-returns-from-customer/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<GRFC>(`/goods-returns-from-customer/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<GRFC>(`/goods-returns-from-customer/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/goods-returns-from-customer/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// =================== Возврат поставщику ===================

export interface GRTSItem {
  id: string;
  line_number: number;
  nomenclature_id: string;
  characteristic_id: string | null;
  series_id?: string | null;
  quantity: string;
  price: string;
  amount_without_vat: string;
  vat_rate_id: string;
  vat_amount: string;
  total_amount: string;
  /** Перебивка рахунку запасів рядка (вкладка «Рахунки обліку»). */
  account_code?: string | null;
}

export interface GRTSListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  base_receipt_id: string | null;
  total_amount: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3b:
  state?: CancellableState;
}

export interface GRTS extends GRTSListItem {
  contract_id: string;
  warehouse_id: string;
  amount_without_vat: string;
  vat_amount: string;
  posted_at: string | null;
  comment: string | null;
  items: GRTSItem[];
  // Block 5.3b:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface GRTSCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  base_receipt_id: string;
  warehouse_id?: string | null;
  comment?: string | null;
  // 2026-07-29: рядки часткового повернення від редактора.
  items?: Omit<GRTSItem, 'id'>[];
}

export const goodsReturnsToSupplier = {
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number; offset?: number; sort_by?: string; sort_dir?: 'asc' | 'desc'; date_from?: string; date_to?: string }) =>
    api.get<Page<GRTSListItem>>('/goods-returns-to-supplier', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<GRTS>(`/goods-returns-to-supplier/${id}`).then((r) => r.data),
  createFromReceipt: (payload: GRTSCreate) =>
    api.post<GRTS>('/goods-returns-to-supplier/from-receipt', payload).then((r) => r.data),
  update: (id: string, payload: Partial<GRTSCreate>) =>
    api.patch<GRTS>(`/goods-returns-to-supplier/${id}`, payload).then((r) => r.data),
  returnable: (receiptId: string) =>
    api.get(`/goods-returns-to-supplier/returnable/${receiptId}`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/goods-returns-to-supplier/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<GRTS>(`/goods-returns-to-supplier/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<GRTS>(`/goods-returns-to-supplier/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/goods-returns-to-supplier/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
